import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banknote, CheckCircle2, Landmark, ShoppingBag } from "lucide-react";
import {
  CHECKOUT_FIELD_MAX,
  ORDER_ROUTES,
  STORE_CURRENCY,
  checkoutFormSchema,
  contentFor,
  formatPrice,
  orderCheckoutResponseSchema,
  type PaymentMethod,
  type Store,
  type StoreItem,
} from "@nh/shared";
import { z } from "zod";
import { authFetch } from "@/shared/api/session";
import { Alert, Button, Input } from "@/shared/ui/core";
import { useFieldErrors } from "@/shared/lib/useFieldErrors";
import { Modal } from "@/shared/ui/ui";
import { useAuth } from "@/features/auth/auth.context";
import { PlaceSearch } from "@/features/panel/address/PlaceSearch";

type CheckoutData = Extract<
  z.infer<typeof orderCheckoutResponseSchema>,
  { ok: true }
>["data"];

/**
 * ═══════════════════════════════════════════════════════════════════
 * La compra EN la tienda personalizada, SIN salir de ella (Anahí,
 * 27-ago: "debe mostrarse un modal para realizar el pago"). Nada de
 * redirigir al checkout de la casa: el pedido se arma aquí —datos de
 * entrega y método— y se paga DIRECTO con quien vende:
 *
 *   · TRANSFERENCIA → la confirmación enseña la cuenta del
 *     franquiciatario (banco, CLABE, titular).
 *   · EFECTIVO → se coordina con él al entregar.
 *
 * La orden nace PENDIENTE; el franquiciatario la valida desde su panel
 * (Registrar pago, con su comprobante) y SOLO entonces se descuenta el
 * stock — el circuito que ya corre por debajo.
 * ═══════════════════════════════════════════════════════════════════
 */
export function StoreCheckoutModal({
  open,
  onClose,
  store,
  item,
  quantity,
  accent,
}: {
  open: boolean;
  onClose: () => void;
  store: Store;
  item: StoreItem;
  quantity: number;
  accent: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [zip, setZip] = useState("");
  /* Los campos de la dirección aparecen cuando hay algo que confirmar:
   * al elegir en el buscador, o al pedir escribirla a mano. */
  const [mostrarCampos, setMostrarCampos] = useState(false);
  const [method, setMethod] = useState<PaymentMethod>("transfer");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckoutData | null>(null);

  /*
   * ── Validación por campo (Anahí, 1-sep) ────────────────────────────
   * Este modal no validaba NADA: los seis campos viajaban al servidor y
   * volvía un solo aviso, sin decir cuál estorbaba. En el momento de la
   * compra eso no es una molestia, es una venta menos.
   *
   * Contra `checkoutFormSchema`, que se compone de las MISMAS piezas que
   * valida el servidor: el mensaje bajo la caja es el que devolvería el
   * API, y no hay dos definiciones de "una calle válida" que puedan
   * separarse.
   */
  const campos = useFieldErrors(checkoutFormSchema);
  const valores = {
    ...(fullName.trim() ? { fullName: fullName.trim() } : {}),
    phone: phone.trim(),
    street: street.trim(),
    city: city.trim(),
    state: stateName.trim(),
    zip: zip.trim(),
  };

  const content = contentFor(item.product.translations, "es");
  const totalCents = (item.priceCents ?? 0) * quantity;

  async function comprar(event: FormEvent) {
    event.preventDefault();
    setError(null);

    /*
     * Los campos de la dirección se pueden estar ocultos —aparecen al
     * elegir en el buscador o al pedir escribirla a mano—. Si falta algo
     * de ahí dentro, se ABREN antes de marcarlos: señalar en rojo un
     * campo que no se ve es dejar a quien compra buscando un error
     * invisible.
     */
    const datos = campos.validar(valores);
    if (!datos) {
      setMostrarCampos(true);
      return;
    }

    setSending(true);
    try {
      const data = await authFetch(ORDER_ROUTES.checkout, orderCheckoutResponseSchema, {
        baseUrl: "",
        method: "POST",
        body: {
          currency: STORE_CURRENCY,
          items: [{ productId: item.product.id, quantity }],
          ...(datos.fullName ? { fullName: datos.fullName } : {}),
          phone: datos.phone,
          shippingAddress: {
            street: datos.street,
            city: datos.city,
            state: datos.state,
            zip: datos.zip,
          },
          paymentMethod: method,
          storeSlug: store.slug,
        },
      });
      setResult(data);
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : t("No se pudo registrar tu pedido. Inténtalo otra vez."),
      );
    } finally {
      setSending(false);
    }
  }

  function cerrar() {
    setResult(null);
    setError(null);
    onClose();
  }

  return (
    <Modal open={open} onClose={cerrar} title={result ? t("¡Pedido registrado!") : t("Tu pedido en {{name}}", { name: store.name })}>
      {!isAuthenticated ? (
        /* ── Sin sesión: la puerta, sin perder a dónde iba ──────────── */
        <div className="grid gap-4 py-2">
          <p className="text-[14px] leading-relaxed text-ds-ink-3">
            {t("Para pedir en {{name}} necesitas tu cuenta — así el pedido queda a tu nombre y puedes seguirlo.", { name: store.name })}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={cerrar}>
              {t("Ahora no")}
            </Button>
            <Button
              style={{ background: accent }}
              className="text-white"
              onClick={() => navigate("/login", { state: { from: location.pathname } })}
            >
              {t("Iniciar sesión")}
            </Button>
          </div>
        </div>
      ) : result ? (
        /* ── La confirmación: el folio y CÓMO pagar ──────────────────── */
        <div className="grid gap-4 py-1">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ds-ok-bg text-ds-ok-t">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <div>
              <p className="font-mono text-[18px] font-semibold text-ds-ink">
                {result.order.folio}
              </p>
              <p className="text-[12.5px] text-ds-ink-3">
                {t("Guarda tu folio. Total: {{total}}", {
                  total: formatPrice(result.order.totalCents, result.order.currency),
                })}
              </p>
            </div>
          </div>

          {method === "transfer" ? (
            result.sellerAccount && "clabe" in result.sellerAccount ? (
              <div className="rounded-2xl bg-ds-mist/50 p-4">
                <p className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-ds-label text-ds-ink-3">
                  <Landmark className="h-4 w-4" style={{ color: accent }} />
                  {t("Transfiere a esta cuenta")}
                </p>
                <dl className="mt-2 grid gap-1 text-[14px]">
                  <div className="flex justify-between gap-3">
                    <dt className="text-ds-ink-3">{t("Banco")}</dt>
                    <dd className="font-medium text-ds-ink">{result.sellerAccount.bankName}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-ds-ink-3">{t("CLABE")}</dt>
                    <dd className="font-mono font-medium text-ds-ink">
                      {result.sellerAccount.clabe}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-ds-ink-3">{t("Titular")}</dt>
                    <dd className="font-medium text-ds-ink">{result.sellerAccount.holderName}</dd>
                  </div>
                </dl>
                <p className="mt-3 text-[12.5px] leading-relaxed text-ds-ink-3">
                  {t("Comparte tu comprobante con {{name}}: al registrar tu pago, tu pedido cambia de estado.", { name: store.ownerName })}
                </p>
              </div>
            ) : (
              <Alert variant="warning">
                {t("La tienda aún no registra su cuenta bancaria: pide los datos por su WhatsApp antes de transferir.")}
              </Alert>
            )
          ) : (
            <p className="rounded-2xl bg-ds-mist/50 px-4 py-3 text-[13.5px] leading-relaxed text-ds-ink-3">
              {t("Pagarás en efectivo: coordina la entrega y el pago directamente con {{name}}.", { name: store.ownerName })}
            </p>
          )}

          <div className="flex justify-end">
            <Button style={{ background: accent }} className="text-white" onClick={cerrar}>
              {t("Listo")}
            </Button>
          </div>
        </div>
      ) : (
        /* ── El pedido: entrega y método, en una sola vista ──────────── */
        <form onSubmit={comprar} className="grid gap-4 py-1">
          <div className="flex items-center justify-between gap-3 rounded-2xl bg-ds-mist/40 px-4 py-3">
            <p className="min-w-0 truncate text-[14px] font-medium text-ds-ink">
              {quantity}× {content.name}
            </p>
            <p className="font-mono text-[15px] font-semibold" style={{ color: accent }}>
              {formatPrice(totalCents, STORE_CURRENCY)}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {/*
              Cada caja lleva su tope del CONTRATO, su error debajo al
              salir, y la limpieza a la primera tecla. Los topes no se
              escriben aquí a mano: un `maxLength` que no coincide con su
              esquema es una caja que deja teclear lo que luego rechazan.
            */}
            <Input
              label={t("Quién recibe (opcional)")}
              value={fullName}
              maxLength={CHECKOUT_FIELD_MAX.fullName}
              error={campos.errors.fullName}
              onChange={(event) => {
                setFullName(event.target.value);
                campos.limpiar("fullName");
              }}
              onBlur={() => campos.revisar("fullName", valores)}
              placeholder={t("Como aparece en tu identificación")}
            />
            <Input
              label={t("Teléfono")}
              required
              inputMode="tel"
              value={phone}
              maxLength={CHECKOUT_FIELD_MAX.phone}
              error={campos.errors.phone}
              onChange={(event) => {
                setPhone(event.target.value);
                campos.limpiar("phone");
              }}
              onBlur={() => campos.revisar("phone", valores)}
              placeholder="55 1234 5678"
            />
          </div>
          {/*
            ── UN solo input para la dirección (Anahí, 29-ago) ──────────
            Mismo patrón que /socio/perfil y las sedes: se busca arriba y
            los campos aparecen YA RELLENOS, como confirmación. Cuatro
            campos vacíos junto a un buscador que los llena es pedir dos
            veces lo mismo, y aquí duele más: es el momento de la compra,
            donde cada campo de más es una venta menos.

            El buscador exige sesión (la clave de Google vive en el
            servidor) y este modal ya la exige, así que siempre funciona.
            Si el servidor no tuviera clave, `onUnavailable` abre los
            campos y el pedido se cierra igual.
          */}
          <PlaceSearch
            label={t("Buscar tu dirección")}
            onPick={(encontrada) => {
              if (encontrada.street) setStreet(encontrada.street);
              if (encontrada.city) setCity(encontrada.city);
              if (encontrada.state) setStateName(encontrada.state);
              if (encontrada.zip) setZip(encontrada.zip);
              setMostrarCampos(true);
            }}
            onUnavailable={() => setMostrarCampos(true)}
          />

          {!mostrarCampos && (
            <button
              type="button"
              onClick={() => setMostrarCampos(true)}
              className="w-fit text-[12.5px] font-medium text-ds-violet-pop hover:underline"
            >
              {t("Escribirla a mano")}
            </button>
          )}

          {mostrarCampos && (
            <>
              <Input
                label={t("Calle, número y colonia")}
                required
                value={street}
                maxLength={CHECKOUT_FIELD_MAX.street}
                error={campos.errors.street}
                onChange={(event) => {
                  setStreet(event.target.value);
                  campos.limpiar("street");
                }}
                onBlur={() => campos.revisar("street", valores)}
                placeholder={t("Av. Siempre Viva 742, Col. Centro")}
              />
              <div className="grid gap-3 sm:grid-cols-3">
                <Input
                  label={t("Ciudad")}
                  required
                  value={city}
                  maxLength={CHECKOUT_FIELD_MAX.city}
                  error={campos.errors.city}
                  onChange={(event) => {
                    setCity(event.target.value);
                    campos.limpiar("city");
                  }}
                  onBlur={() => campos.revisar("city", valores)}
                />
                <Input
                  label={t("Estado")}
                  required
                  value={stateName}
                  maxLength={CHECKOUT_FIELD_MAX.state}
                  error={campos.errors.state}
                  onChange={(event) => {
                    setStateName(event.target.value);
                    campos.limpiar("state");
                  }}
                  onBlur={() => campos.revisar("state", valores)}
                />
                <Input
                  label={t("Código postal")}
                  required
                  inputMode="numeric"
                  maxLength={CHECKOUT_FIELD_MAX.zip}
                  error={campos.errors.zip}
                  value={zip}
                  /* Lo que no es dígito no llega ni a entrar. */
                  onChange={(event) => {
                    setZip(event.target.value.replace(/\D/g, ""));
                    campos.limpiar("zip");
                  }}
                  onBlur={() => campos.revisar("zip", valores)}
                />
              </div>
            </>
          )}

          {/* ── El método: solo los dos de la tienda ──────────────────── */}
          <fieldset className="grid gap-2 sm:grid-cols-2">
            <legend className="sr-only">{t("Método de pago")}</legend>
            {(
              [
                { value: "transfer", icon: Landmark, label: t("Transferencia"), hint: t("A la cuenta de la tienda") },
                { value: "cash", icon: Banknote, label: t("Efectivo"), hint: t("Al recibir tu pedido") },
              ] as const
            ).map(({ value, icon: Icon, label, hint }) => {
              const active = method === value;
              return (
                <label
                  key={value}
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition ${
                    active ? "bg-ds-mist/40" : "border-ds-border bg-white hover:border-ds-ink-3/40"
                  }`}
                  style={active ? { borderColor: accent } : undefined}
                >
                  <input
                    type="radio"
                    name="storePaymentMethod"
                    value={value}
                    checked={active}
                    onChange={() => setMethod(value)}
                    className="h-4 w-4"
                    style={{ accentColor: accent }}
                  />
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
                    style={{ background: active ? accent : "#B9B4CF" }}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="grid">
                    <span className="text-[14px] font-semibold text-ds-ink">{label}</span>
                    <span className="text-[12px] text-ds-ink-3">{hint}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={cerrar} type="button">
              {t("Cancelar")}
            </Button>
            <button
              type="submit"
              disabled={sending}
              className="inline-flex h-10 items-center gap-2 rounded-full px-6 text-[13.5px] font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
              style={{ background: accent }}
            >
              <ShoppingBag className="h-4 w-4" />
              {sending ? t("Registrando…") : t("Confirmar pedido")}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
